import {
  IconName,
  useGenericFilter,
  type ClientSideFilterRule,
  type DateRangeFilter,
  type FilterableProperty,
  type FixingIndexMarkMessageData,
  type UsePersistedBlotterTable,
} from '@talos/kyoko';
import { pick } from 'lodash-es';
import { useMemo } from 'react';

export interface IndexMarksFilter extends DateRangeFilter {
  Index?: string[];
}

interface UseIndexMarksFilterParams {
  persistedBlotterTable: UsePersistedBlotterTable;
}

const INDEX_MARKS_FILTER_RULES: ClientSideFilterRule<IndexMarksFilter>[] = [
  {
    type: 'include',
    filterKey: 'Index',
    dataKey: 'Index',
  },
];

export function useIndexMarksFilter({ persistedBlotterTable }: UseIndexMarksFilterParams) {
  const filterableProperties = useMemo(
    () =>
      [
        {
          key: 'Index',
          label: 'Index Name',
          icon: IconName.Hashtag,
          control: 'text',
        },
      ] satisfies FilterableProperty[],
    []
  );

  return useGenericFilter<IndexMarksFilter>(filterableProperties, {
    initialFilter: persistedBlotterTable.initialState?.filter,
    onFilterChanged: persistedBlotterTable.onFilterChanged,
    clientSideFilterRules: INDEX_MARKS_FILTER_RULES,
  });
}

export function onlyServerFilterKeys(filter?: IndexMarksFilter) {
  if (filter == null) {
    return filter;
  }

  return pick(filter, ['StartDate', 'EndDate']);
}

const colIDToFilterBuilderKeyMap = {
  Index: 'Index',
  IndexDisplayName: 'Index',
} as const satisfies Partial<Record<keyof FixingIndexMarkMessageData | 'IndexDisplayName', keyof IndexMarksFilter>>;

export function colIDToFilterBuilderKey(id: string): keyof IndexMarksFilter | undefined {
  return colIDToFilterBuilderKeyMap[id as keyof typeof colIDToFilterBuilderKeyMap] as
    | keyof IndexMarksFilter
    | undefined;
}
